import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence, useInView, animate } from "framer-motion";
import Cal, { getCalApi } from "@calcom/embed-react";
import { Link } from "react-router-dom";
import { ConciergeHero, DemoSection } from "@/components/ConciergeDemo";
import { VoiceDemo } from "@/components/VoiceDemo";
import TeachDemo from "@/components/TeachDemo";
import { FAQ, CATEGORIES } from "./FAQ";
import Seo, { SITE_URL, faqLd } from "@/components/Seo";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  MessageSquareText, ShieldCheck, FileText, Users, Bell, BarChart3,
  Sparkles, ArrowRight, Check, Phone, BadgeCheck,
  ShieldAlert, Megaphone, Clock, Headset,
  Zap, Menu, X, Gavel,
  Repeat, ScanText, HeartHandshake, UserRoundCheck,
  Building2, MessagesSquare, BookOpen, TrendingUp, Lightbulb, Languages,
  ClipboardList, CalendarCheck, PenLine, CalendarDays, Scale, Contact, Globe, FileBarChart, UserRound, History,
  HardHat, FileSpreadsheet, Route, Download, Loader2, Home, MapPin, KeyRound, Waves,
} from "lucide-react";

const fade = { hidden: { opacity: 0, y: 20 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] } }) };

const NAV_LINKS = [
  ["#managers", "For managers"], ["#where", "Communities"], ["#platform", "Platform"],
  ["#overview", "Overview"], ["#results", "ROI"], ["#faq", "FAQ"],
];

const CAL_LINK = "mdawson40/30min";
const CAL_NS = "30min";
const CAL_ATTRS = { "data-cal-namespace": CAL_NS, "data-cal-link": CAL_LINK, "data-cal-config": '{"layout":"month_view"}' };


function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <nav className="sticky top-0 z-50 bg-sand-50/95 backdrop-blur border-b border-sand-200">
      <div className="max-w-6xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-2.5 shrink-0">
          <div className="h-9 w-9 rounded-xl bg-redline-500 flex items-center justify-center shrink-0">
            <MessageSquareText className="h-5 w-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="leading-none">
            <div className="font-heading font-extrabold tracking-tight text-ink text-[17px]">Ask Redline</div>
          </div>
        </a>

        <div className="hidden lg:flex items-center gap-6 text-sm font-semibold text-ink-700">
          {NAV_LINKS.map(([h, l]) => <a key={h} href={h} className="hover:text-redline-600 transition-colors">{l}</a>)}
        </div>

        <div className="hidden lg:flex items-center gap-4 shrink-0">
          <Link to="/login" data-testid="nav-signin-btn" className="text-sm font-semibold text-ink-500 hover:text-ink transition-colors">Sign in</Link>
          <a href="#contact" {...CAL_ATTRS} data-testid="nav-book-demo-btn" className="rounded-full bg-redline-500 text-white text-sm font-semibold px-5 py-2.5 hover:bg-redline-600 transition-colors">Book a demo</a>
        </div>

        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)} aria-label="Menu"
          className="lg:hidden -mr-1.5 h-10 w-10 grid place-items-center rounded-lg text-ink hover:bg-sand-100 transition-colors">
          {open ? <X className="h-6 w-6" strokeWidth={2} /> : <Menu className="h-6 w-6" strokeWidth={2} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div data-testid="nav-mobile-menu" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }} className="lg:hidden overflow-hidden border-t border-sand-200 bg-sand-50">
            <div className="px-5 sm:px-6 py-3 flex flex-col">
              {NAV_LINKS.map(([h, l]) => (
                <a key={h} href={h} onClick={() => setOpen(false)}
                  className="py-3 text-[15px] font-semibold text-ink-800 hover:text-redline-600 transition-colors border-b border-sand-200/70 last:border-0">{l}</a>
              ))}
              <div className="flex items-center gap-3 pt-4 pb-2">
                <Link to="/login" data-testid="nav-mobile-signin" onClick={() => setOpen(false)}
                  className="flex-1 text-center rounded-full border border-sand-200 text-ink font-semibold px-5 py-3 hover:border-ink transition-colors">Sign in</Link>
                <a href="#contact" {...CAL_ATTRS} onClick={() => setOpen(false)}
                  className="flex-1 text-center rounded-full bg-redline-500 text-white font-semibold px-5 py-3 hover:bg-redline-600 transition-colors">Book a demo</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

/* Interactive portfolio ROI multiplier — scales the 977-request study across a management company's communities */
function PortfolioCalculator() {
  const [count, setCount] = useState(50);
  // Per community, per year (from the 977-request study): ~250 requests (92% AI-addressable = 230),
  // ~734 staff responses (92% = ~675 AI can handle/prepare), ~28 staff hours saved (conservative, written-message model).
  const requests = 230 * count;
  const responses = 675 * count;
  const hours = 28 * count;
  const workdays = Math.round(hours / 8);
  const fmt = (n) => n.toLocaleString();
  const stats = [
    { icon: MessageSquareText, value: fmt(requests), label: "resident requests answered or prepared by AI, every year" },
    { icon: Repeat, value: fmt(responses), label: "staff replies lifted off your managers' plates, every year" },
    { icon: Clock, value: fmt(hours), suffix: " hrs", label: `staff time returned, every year — about ${workdays.toLocaleString()} full workdays` },
  ];
  return (
    <div data-testid="portfolio-calculator" className="mt-5 relative overflow-hidden rounded-3xl bg-ink text-white p-8 md:p-10">
      <div className="absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-redline-500/20 blur-3xl" />
      <div className="relative grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="lg:col-span-4">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-4"><Building2 className="h-3.5 w-3.5" strokeWidth={2} /> Portfolio impact</span>
          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Your whole portfolio, on autopilot.</h3>
          <p className="mt-3 text-sand-300 text-sm leading-relaxed">Set your portfolio size and watch the workload the concierge lifts off your team, year after year.</p>
          <div className="mt-6">
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-sm font-semibold text-sand-200">Communities you manage</span>
              <span data-testid="portfolio-count" className="font-heading text-3xl font-extrabold text-redline-400 tabular-nums">{count.toLocaleString()}</span>
            </div>
            <input type="range" min="1" max="5000" value={count} onChange={(e) => setCount(Number(e.target.value))}
              data-testid="portfolio-slider" aria-label="Communities you manage"
              className="w-full h-2 rounded-full cursor-pointer accent-redline-500" />
            <div className="flex justify-between text-[11px] text-sand-400 mt-1.5"><span>1</span><span>5,000+ communities</span></div>
            <div className="mt-4 flex flex-wrap gap-2">
              {[10, 50, 250, 1000, 5000].map((n) => (
                <button key={n} type="button" onClick={() => setCount(n)} data-testid={`portfolio-preset-${n}`}
                  className={`rounded-full px-3 py-1.5 text-[12px] font-semibold transition-colors ${count === n ? "bg-redline-500 text-white" : "bg-white/10 text-sand-200 hover:bg-white/15"}`}>
                  {n.toLocaleString()}
                </button>
              ))}
            </div>
            <p className="mt-3 text-[12px] text-sand-400">Whether you run five communities or five thousand, the concierge scales the same way <span className="text-white font-semibold">with no added headcount</span>.</p>
          </div>
        </div>
        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl bg-white/[0.05] border border-white/10 p-6 flex flex-col">
              <div className="h-10 w-10 rounded-xl bg-redline-500/20 flex items-center justify-center mb-4"><s.icon className="h-5 w-5 text-redline-400" strokeWidth={1.75} /></div>
              <div className="font-heading text-[1.9rem] sm:text-[2.1rem] leading-none font-extrabold tracking-tight text-white tabular-nums transition-all duration-300">{s.value}<span className="text-redline-400 text-xl">{s.suffix || ""}</span></div>
              <p className="text-sand-300 text-[13px] mt-3 leading-snug">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
      <p className="relative mt-6 text-[11px] text-sand-400 leading-relaxed max-w-3xl">Projected from the 977-request study (~734 staff responses and 92% AI-addressable per community per year) using conservative savings assumptions. Counts written messages only — it excludes phone calls, document searches, work-order creation, vendor follow-up, after-hours coverage and resident wait time, so real impact runs higher.</p>
    </div>
  );
}

/* animated count-up used across the credibility band */
function CountUp({ to, suffix = "", decimals = 0, duration = 1.7 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, { duration, ease: [0.16, 1, 0.3, 1], onUpdate: (v) => setVal(v) });
    return () => controls.stop();
  }, [inView, to, duration]);
  const display = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString();
  return <span ref={ref} className="tabular-nums">{display}{suffix}</span>;
}

/* HERO STAT BAND — the industry reality from the deck: service is the business, and it's breaking */
function HeroStatBand() {
  const stats = [
    { value: 66, suffix: "%", label: "of boards that switch managers blame poor responsiveness & communication" },
    { value: 71, suffix: "%", label: "of community managers report moderate-to-severe job burnout" },
    { value: 97, suffix: "%", label: "of management-company leaders say the manager shortage is hurting growth" },
    { value: 20, suffix: "%", label: "of the community-management workforce turns over in a typical year" },
  ];
  return (
    <section className="relative overflow-hidden bg-ink" data-testid="hero-stat-band">
      <div className="grain absolute inset-0 opacity-[0.05]" />
      <div className="absolute -left-24 top-1/2 -translate-y-1/2 h-64 w-64 rounded-full bg-redline-500/15 blur-3xl" />
      <div className="relative max-w-6xl mx-auto px-6 py-14">
        <div className="max-w-3xl mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">Service <span className="text-redline-400">is</span> the business — and it's the part that's breaking.</h2>
          <p className="mt-3 text-sand-300 text-sm leading-relaxed">Boards rarely leave over price. They leave when residents don't hear back. And the managers expected to answer are stretched past the limit — which is exactly the gap a concierge closes.</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
          {stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`} className="border-l-2 border-redline-500/60 pl-5">
              <div className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-none text-redline-400">
                {s.text ? <span className="text-white">{s.text}</span> : <CountUp to={s.value} suffix={s.suffix || ""} />}
              </div>
              <p className="text-sand-300 text-[13px] leading-snug mt-3 max-w-[15rem]">{s.label}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-x-8 gap-y-3">
          <p className="text-white font-heading font-bold text-[15px] flex-1">The fix isn't hiring more managers you can't find — it's giving the ones you have a 24/7 concierge and a co-pilot.</p>
          <span className="text-sand-400 text-[11px] leading-relaxed sm:max-w-[15rem]">Sources: industry surveys of HOA boards, community managers &amp; management-company executives.</span>
        </div>
      </div>
    </section>
  );
}

/* MANAGER TOOLKIT — the co-pilot every community manager gets (the deck's manager story) */
const MANAGER_TOOLS = [
  { icon: MessagesSquare, title: "Ask across every community", body: "One place to ask “which communities have an open water issue?” or “what's Maplewood's pool rule?” — answered from each community's own documents and live activity." },
  { icon: Lightbulb, title: "Teach it once", body: "When the AI meets a question it can't answer, it asks you — you reply in a sentence, and it knows that answer for that community forever. No FAQ to hand-maintain." },
  { icon: PenLine, title: "Draft it for me", body: "A late-dues notice, a violation letter, a board update — describe it once and get a ready-to-send draft in the community's voice. You approve; it sends." },
  { icon: Repeat, title: "The routine, handled", body: "Repetitive resident questions and intake answer themselves 24/7, so managers spend the day on decisions and vendors — not copy-pasting the same reply." },
  { icon: Bell, title: "Know before the board does", body: "Community Pulse surfaces the community heating up — rising complaints, a stalled request, slipping sentiment — before it lands in your inbox as a problem." },
  { icon: FileBarChart, title: "Board-ready reporting", body: "One tap turns a month of activity into a clean, board-ready report — what was asked, handled and resolved — the proof of value your renewals depend on." },
];

function ManagerToolkit() {
  return (
    <section id="managers" className="scroll-mt-20 bg-white border-y border-sand-200 py-24" data-testid="managers-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">For your managers</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Every manager gets <span className="text-redline-500">a co-pilot.</span></h2>
          <p className="mt-4 text-ink-700 leading-relaxed">Ask Redline doesn't replace your managers — it clears their plate so each one can cover more doors without drowning. Here's the toolkit that comes with every seat.</p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {MANAGER_TOOLS.map((t, i) => (
            <motion.div key={t.title} custom={i % 3} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
              data-testid={`manager-tool-${i}`}
              className="rounded-3xl border border-sand-200 bg-white p-7 hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(15,15,20,0.25)] transition-all duration-300">
              <div className="h-12 w-12 rounded-2xl bg-redline-50 flex items-center justify-center mb-5"><t.icon className="h-6 w-6 text-redline-600" strokeWidth={1.5} /></div>
              <h3 className="font-heading text-lg font-extrabold tracking-tight mb-2">{t.title}</h3>
              <p className="text-ink-700 text-sm leading-relaxed">{t.body}</p>
            </motion.div>
          ))}
        </div>

        {/* Teach-it-once showcase — reuses the live teach widget */}
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
          data-testid="manager-teach-showcase" className="mt-6 grid lg:grid-cols-5 gap-6 items-center rounded-3xl border border-sand-200 bg-sand-50 p-7 md:p-8">
          <div className="lg:col-span-2">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 rounded-full px-2.5 py-1 mb-4"><Lightbulb className="h-3.5 w-3.5" strokeWidth={2} /> Teach it once</span>
            <h3 className="font-heading text-2xl font-extrabold tracking-tight">Your managers train it in a sentence.</h3>
            <p className="mt-3 text-ink-700 text-sm leading-relaxed">The assistant tells you what residents keep asking. Answer once, and it knows it forever — so the concierge keeps getting sharper without anyone maintaining a knowledge base.</p>
          </div>
          <div className="lg:col-span-3 rounded-2xl border border-sand-200 bg-white p-6"><TeachDemo /></div>
        </motion.div>
      </div>
    </section>
  );
}

/* WHERE IT WORKS — property types from the deck; every visitor sees their own community */
const PROPERTY_TYPES = [
  { icon: Building2, name: "Condos & co-ops", body: "High-density, high-question. Access, amenities, leasing and rules — answered instantly." },
  { icon: Home, name: "Single-family HOAs", body: "CC&Rs, ARC requests and violations — cited and consistent every single time." },
  { icon: MapPin, name: "Master-planned communities", body: "Many sub-associations, one standard. Each neighborhood keeps its own rules and line." },
  { icon: Waves, name: "Amenity-heavy communities", body: "Pools, courts and clubhouses — hours and reservations handled by text, 24/7." },
  { icon: Users, name: "Active adult & 55+", body: "Phone-first residents get a real voice on the line — no app to download." },
  { icon: KeyRound, name: "High-renter populations", body: "Constant move-ins and turnover intake, without burying your managers." },
  { icon: HardHat, name: "Builders & developers", body: "Stand up a new community's concierge before the first resident moves in." },
];

function WhereItWorks() {
  return (
    <section id="where" className="scroll-mt-20 relative overflow-hidden bg-gradient-to-b from-sand-50 to-white py-24" data-testid="where-section">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">Where it works</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Built for how your <span className="text-redline-500">communities actually run.</span></h2>
          <p className="mt-4 text-ink-700 leading-relaxed">Every community type asks in its own way. Ask Redline meets each one where it is — grounded in that community's own documents and rules.</p>
        </motion.div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PROPERTY_TYPES.map((p, i) => (
            <motion.div key={p.name} custom={i % 3} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
              data-testid={`property-type-${i}`}
              className="rounded-2xl border border-sand-200 bg-white p-6 hover:border-ink/15 hover:-translate-y-0.5 transition-all duration-300">
              <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center mb-4"><p.icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
              <h3 className="font-heading font-extrabold tracking-tight text-[15px] mb-1.5">{p.name}</h3>
              <p className="text-ink-600 text-[13px] leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
          {/* Portfolio rollup — the management-company payoff */}
          <motion.div custom={1} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
            data-testid="property-type-portfolio" className="relative overflow-hidden rounded-2xl bg-ink text-white p-6 flex flex-col justify-center">
            <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-redline-500/25 blur-3xl" />
            <div className="relative h-10 w-10 rounded-xl bg-redline-500/20 flex items-center justify-center mb-4"><Building2 className="h-5 w-5 text-redline-400" strokeWidth={1.75} /></div>
            <h3 className="relative font-heading font-extrabold tracking-tight text-[15px] mb-1.5">Whole portfolios</h3>
            <p className="relative text-sand-300 text-[13px] leading-relaxed">Run 5 or 5,000. Every community gets its own line, documents and rules — all rolled up under one dashboard you manage, on your brand.</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* FIELD SERVICES — the operations half: what happens AFTER the answer.
   Written for the manager, with one line for the board (owner's call). */
const FS_CHAIN = [
  {
    icon: ClipboardList, tag: "The request becomes a commitment",
    title: "One coded work order behind every vendor job",
    body: "A resident's text, a recurring visit, an awarded project — all of it lands on the same spine with a budget code attached. Nothing gets promised to a vendor that your books can't see.",
  },
  {
    icon: ShieldCheck, tag: "Insurance that actually gates the work",
    title: "A lapsed certificate takes the crew off the property",
    body: "Come out for a site visit with nothing on file — that's fine, it keeps the funnel alive. Start live work uninsured, and it won't dispatch. If a certificate lapses mid-job, the job pauses until it's replaced.",
  },
  {
    icon: HardHat, tag: "Straight to the person on site",
    title: "The crew gets a text, in their language",
    body: "Not the office, then a phone tree, then a callback — the crew member assigned to that property, in English or Spanish. They can text back \"done\" with a photo, or call the community line and talk to it. The photo closes the resident's ticket.",
  },
  {
    icon: FileSpreadsheet, tag: "Money, with the paperwork already done",
    title: "Invoices arrive coded, checked and approved",
    body: "Post a project, vendors bid from a link with no login and no account. Award it and the kickoff refuses to start without a budget line. Invoices get read, proof-checked against the work order, approved, and exported to your accounting system. Redline never touches the money.",
  },
];

function FieldServices() {
  return (
    <section id="field-services" className="relative overflow-hidden bg-white py-24" data-testid="field-services-section">
      <div className="absolute inset-y-0 left-0 w-1/2 bg-sand-50/70 hidden lg:block" />
      <div className="max-w-6xl mx-auto px-6 relative grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">
              <Route className="h-4 w-4" strokeWidth={2} /> Field services · new
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]">
              Answering the question is half of it.<br />
              <span className="text-redline-500">Somebody still has to fix it.</span>
            </h2>
            <p className="mt-5 text-ink-700 leading-relaxed">
              Most of your day isn't answering — it's chasing. Which vendor covers this. Whether their
              insurance is current. Who's actually going out. Whether it got done. Where the invoice went.
              Field Services runs that whole chain inside the same platform the concierge already lives in.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <a href="#contact" {...CAL_ATTRS} data-testid="field-services-cta"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-ink text-white font-semibold px-6 py-3 text-sm hover:bg-ink-700 transition-colors">
                See it on your vendors <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </a>
              <Link to="/field-services" data-testid="field-services-page-link"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-sand-300 text-ink font-semibold px-6 py-3 text-sm hover:border-ink transition-colors">
                The full breakdown
              </Link>
            </div>
            <p className="mt-7 text-sm text-ink-600 leading-relaxed border-l-2 border-redline-500/40 pl-4">
              <span className="font-semibold text-ink">For the board:</span> what was spent, what needed
              their signature above the community's threshold, and photo proof it happened — without
              anyone chasing a manager for it.
            </p>
            <p className="mt-4 text-[13px] text-ink-500 leading-relaxed pl-4">
              Available as an add-on module, per community. Redline never moves your money.
            </p>
          </motion.div>
        </div>

        <div className="lg:col-span-7">
          <ol className="relative border-l border-sand-300 ml-5 lg:ml-0 space-y-8">
            {FS_CHAIN.map((s, i) => (
              <motion.li key={s.title} custom={i} variants={fade} initial="hidden" whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`fs-chain-${i}`} className="relative pl-8 sm:pl-10">
                <span className="absolute -left-[17px] top-0 h-[34px] w-[34px] rounded-xl bg-white border border-sand-300 flex items-center justify-center shadow-sm">
                  <s.icon className="h-4 w-4 text-redline-600" strokeWidth={1.75} />
                </span>
                <p className="text-[11px] uppercase tracking-[0.14em] font-bold text-ink-500">{s.tag}</p>
                <h3 className="font-heading text-lg md:text-lg font-extrabold tracking-tight mt-1.5">{s.title}</h3>
                <p className="text-ink-700 text-sm leading-relaxed mt-2 max-w-xl">{s.body}</p>
              </motion.li>
            ))}
          </ol>
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
            data-testid="fs-nothing-rots"
            className="mt-8 ml-5 lg:ml-0 rounded-2xl border border-sand-200 bg-sand-50 p-6">
            <div className="flex items-start gap-3">
              <Bell className="h-5 w-5 text-redline-600 mt-0.5 shrink-0" strokeWidth={1.75} />
              <p className="text-sm text-ink-700 leading-relaxed">
                <span className="font-semibold text-ink">And nothing is allowed to rot.</span> A project that
                goes quiet with no bids, a company half-way through setup, a certificate about to lapse, a job
                nobody accepted — each one gets chased on a schedule, then flagged for a person. Silence is
                never mistaken for progress.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}


function OverviewSection() {
  const [form, setForm] = useState({ name: "", email: "", company: "" });
  const [sending, setSending] = useState(false);

  const getDeck = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      const res = await api.post("/marketing/deck-lead", form, { responseType: "blob" });
      const fname = res.headers?.["x-deck-filename"] || "Ask Redline Enterprise Deck.pdf";
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Here's your deck — thanks for your interest!");
      setForm({ name: "", email: "", company: "" });
    } catch (err) {
      toast.error(err?.response?.status === 429 ? "You've already grabbed it — check your downloads." : "Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <section id="overview" className="scroll-mt-20 relative overflow-hidden bg-ink text-white py-24" data-testid="overview-section">
      <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-redline-500/20 blur-3xl" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-400 mb-3">Watch the overview</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.1]">See Ask Redline in <span className="text-redline-400">five minutes</span>.</h2>
          <p className="mt-4 text-sand-300 leading-relaxed">A narrated walk-through of how the concierge answers residents, lifts work off your team, and gives the board proof of value — the same story as our deck, with audio and transitions.</p>
        </div>
        <div className="mt-10 grid lg:grid-cols-3 gap-6 items-start">
          <div className="lg:col-span-2 rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-ink/40 bg-black">
            <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
              <iframe data-testid="overview-video" className="absolute inset-0 h-full w-full"
                src="https://www.youtube.com/embed/Dd18DYTCVpE"
                title="Ask Redline — Enterprise overview"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen />
            </div>
          </div>
          <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 sm:p-7" data-testid="deck-gate-card">
            <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide rounded-full px-2.5 py-1 bg-redline-500/20 text-redline-300"><FileText className="h-3.5 w-3.5" strokeWidth={2.25} /> The deck</div>
            <h3 className="font-heading text-xl font-extrabold mt-4">Get the full deck (PDF)</h3>
            <p className="text-sand-300 text-sm mt-1.5 leading-relaxed">The same presentation to keep and share with your board. Tell us where to send it and it's yours.</p>
            <form onSubmit={getDeck} className="mt-5 space-y-2.5">
              <input data-testid="deck-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" autoComplete="name" maxLength={200}
                className="w-full rounded-xl bg-white/5 border border-white/15 px-3.5 py-2.5 text-sm text-white placeholder:text-sand-400 outline-none focus:border-redline-400 transition-colors" />
              <input data-testid="deck-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} type="email" placeholder="Work email" autoComplete="email" maxLength={200}
                className="w-full rounded-xl bg-white/5 border border-white/15 px-3.5 py-2.5 text-sm text-white placeholder:text-sand-400 outline-none focus:border-redline-400 transition-colors" />
              <input data-testid="deck-company" value={form.company} onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))} placeholder="Management company (optional)" autoComplete="organization" maxLength={200}
                className="w-full rounded-xl bg-white/5 border border-white/15 px-3.5 py-2.5 text-sm text-white placeholder:text-sand-400 outline-none focus:border-redline-400 transition-colors" />
              <button type="submit" disabled={sending} data-testid="deck-submit"
                className="w-full rounded-full bg-redline-500 text-white text-sm font-semibold px-5 py-3 hover:bg-redline-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" strokeWidth={2.5} />}
                {sending ? "Preparing…" : "Download the deck"}
              </button>
            </form>
            <p className="text-[11px] text-sand-400 mt-3 leading-relaxed">We'll only use this to follow up about Ask Redline. No spam.</p>
          </div>
        </div>
      </div>
    </section>
  );
}


export default function Landing() {
  const [form, setForm] = useState({ name: "", email: "", community: "", message: "" });
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cal = await getCalApi({ namespace: CAL_NS });
        cal("ui", { hideEventTypeDetails: false, layout: "month_view", cssVarsPerTheme: { light: { "cal-brand": "#E11D2A" }, dark: { "cal-brand": "#E11D2A" } } });
      } catch (e) {}
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email."); return; }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! We'll reach out to book your demo shortly.");
      setForm({ name: "", email: "", community: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSending(false); }
  };

  return (
    <div className="font-body bg-sand-50 text-ink">
      <Seo
        title="Ask Redline — HOA Communication Software & AI Text/Call Concierge"
        description="AI-powered HOA communication software and a 24/7 AI answering service for community associations. Residents text or call one number for instant, cited answers from your governing documents — no app required."
        canonical="/classic"
        noindex
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Ask Redline",
            url: SITE_URL,
            logo: `${SITE_URL}/apple-touch-icon.png`,
            description: "HOA communication software and AI text & call concierge for community associations.",
          },
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Ask Redline",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web, SMS, Voice (no app required)",
            description: "AI concierge and communication platform for HOAs and community associations — answers residents by text and phone, grounded in governing documents.",
          },
          faqLd(CATEGORIES.map((c) => c.items[0])),
        ]}
      />
      <Nav />

      {/* HERO */}
      <ConciergeHero onVoice={() => setVoiceOpen(true)} ctaAttrs={{ href: "#contact", ...CAL_ATTRS }} />

      {/* CREDIBILITY BAND — industry-reality stats */}
      <HeroStatBand />

      {/* INTERACTIVE DEMO */}
      <DemoSection onVoice={() => setVoiceOpen(true)} />

      {/* 🆕 MANAGER TOOLKIT — the co-pilot every manager gets */}
      <ManagerToolkit />

      {/* 🆕 WHERE IT WORKS — property types */}
      <WhereItWorks />

      {/* WATCH THE OVERVIEW — ungated video + email-gated deck download */}
      <OverviewSection />


      {/* THE MATH — data-driven ROI, management-company positioning (from 977-request analysis) */}
      <section id="results" className="max-w-6xl mx-auto px-6 py-24">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">The math · analyzed from 977 real resident requests</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Your managers answer the same requests over and over. <span className="text-redline-500">AI takes 92% off their plate.</span></h2>
          <p className="mt-4 text-ink-700 leading-relaxed">We analyzed 904 pages and classified every one of 977 resident request threads from a live community — 5,079 messages in all. Here is what your team is really spending its day on, and how much of it a concierge handles automatically.</p>
        </div>

        {/* Hero 92% + the four hidden-cost stats */}
        <div className="mt-10 grid lg:grid-cols-5 gap-5">
          {/* Big 92% card with stacked breakdown */}
          <motion.div data-testid="roi-hero-92" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-2 relative overflow-hidden rounded-3xl bg-ink text-white p-8 shadow-2xl shadow-ink/25">
            <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-redline-500/25 blur-3xl" />
            <span className="relative inline-flex items-center gap-1.5 text-[10.5px] font-bold uppercase tracking-[0.14em] rounded-full px-2.5 py-1 bg-white/10 text-white">
              <Zap className="h-3.5 w-3.5" strokeWidth={2.25} /> Automation opportunity
            </span>
            <div className="relative font-heading text-[5rem] leading-none font-extrabold tracking-tight mt-5 text-redline-400">92%</div>
            <p className="relative text-sand-300 text-sm mt-3 leading-snug">of resident requests AI can <span className="text-white font-semibold">fully resolve or substantially prepare</span> before a manager is ever involved.</p>
            {/* stacked breakdown bar */}
            <div className="relative mt-7 h-3 rounded-full overflow-hidden bg-white/10 flex">
              <motion.div className="h-full bg-redline-400" initial={{ width: 0 }} whileInView={{ width: "45%" }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] }} />
              <motion.div className="h-full bg-redline-400/45" initial={{ width: 0 }} whileInView={{ width: "47%" }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1, delay: 0.45, ease: [0.16, 1, 0.3, 1] }} />
            </div>
            <div className="relative mt-4 space-y-2 text-[13px]">
              {[
                ["45%", "Fully AI-resolvable", "Access, policies, leasing, documents, payments, account questions", "bg-redline-400"],
                ["47%", "AI-assisted", "Maintenance & incidents — AI collects details, photos, opens & routes the case", "bg-redline-400/45"],
                ["8%", "Human-led", "Waivers, disputes, exceptions, legal review, board decisions", "bg-white/25"],
              ].map(([pct, name, desc, dot]) => (
                <div key={name} className="flex items-start gap-2.5">
                  <span className={`mt-1.5 h-2.5 w-2.5 rounded-full shrink-0 ${dot}`} />
                  <span className="text-sand-200"><span className="font-bold text-white">{pct}</span> {name} <span className="text-sand-400">— {desc}</span></span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Four hidden-cost stats */}
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {[
              { stat: "2.94", icon: MessageSquareText, tag: "Response load", label: "written staff responses per request", sub: "Nearly three replies for every request — most of it administrative back-and-forth." },
              { stat: "42%", icon: Clock, tag: "Slow today", label: "get a same-day answer today", sub: "Residents wait a day or more. The concierge answers instantly, 24/7." },
              { stat: "39%", icon: Repeat, tag: "Chasing replies", label: "had to message again after a reply", sub: "Nearly 4 in 10 residents followed up because the first answer was not enough." },
              { stat: "50%", icon: MessagesSquare, tag: "Long threads", label: "became 4+ message conversations", sub: "Half of all requests spiral into long threads. AI cuts the back-and-forth." },
            ].map((s, i) => (
              <motion.div key={s.stat} data-testid={`roi-stat-${s.stat.replace(/[^a-z0-9]/gi, "")}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="rounded-3xl bg-white border border-sand-200 p-6 hover:border-ink/15 hover:-translate-y-1 transition-all flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center"><s.icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.13em] text-ink-400">{s.tag}</span>
                </div>
                <div className="font-heading text-4xl font-extrabold tracking-tight text-ink mt-4">{s.stat}</div>
                <p className="text-[13px] font-semibold text-ink-800 mt-1 leading-snug">{s.label}</p>
                <p className="text-[12.5px] text-ink-600 mt-2 leading-relaxed">{s.sub}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Where the time goes — request themes */}
        <div data-testid="roi-themes" className="mt-5 rounded-3xl border border-sand-200 bg-white p-8 md:p-10">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-2">Where the time actually goes</p>
            <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">It is not just FAQs — it is the whole operation.</h3>
            <p className="mt-3 text-ink-700 text-sm leading-relaxed">The 977 requests, by theme. Every one of these is something the concierge can answer, intake or route.</p>
          </div>
          <div className="mt-8 grid md:grid-cols-2 gap-x-10 gap-y-4">
            {[
              ["HVAC, electrical & building maintenance", 36],
              ["Access, keys, fobs & callbox", 25],
              ["Amenities, pets, parking & rules", 18],
              ["Documents, forms & account support", 16],
              ["Leasing, tenants & move-ins", 15],
              ["Payments, dues, statements & fines", 11],
              ["Insurance, leaks, water & mold", 7],
              ["Security, incidents & footage", 6],
            ].map(([name, pct], i) => (
              <div key={name} className="flex items-center gap-4">
                <span className="flex-1 text-sm font-medium text-ink-800 leading-snug">{name}</span>
                <div className="w-28 sm:w-40 h-2 rounded-full bg-sand-100 overflow-hidden shrink-0">
                  <motion.div className="h-full rounded-full bg-redline-500" initial={{ width: 0 }} whileInView={{ width: `${(pct / 36) * 100}%` }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.9, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }} />
                </div>
                <span className="w-9 text-right font-heading font-extrabold text-ink tabular-nums text-sm shrink-0">{pct}%</span>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">Themes overlap because a single request can involve more than one issue.</p>
        </div>

        {/* Positioning + portfolio multiplier */}
        <div className="mt-5 grid lg:grid-cols-2 gap-5">
          <div className="rounded-3xl border border-sand-200 bg-sand-50 p-8 flex flex-col justify-center">
            <ShieldCheck className="h-8 w-8 text-redline-500 mb-4" strokeWidth={1.5} />
            <h3 className="font-heading text-xl font-extrabold tracking-tight">It does not replace your manager. It clears their plate.</h3>
            <p className="mt-3 text-ink-700 text-sm leading-relaxed">AI removes the repetitive questions, incomplete requests and endless administrative back-and-forth — so your managers spend their time on decisions, vendors and residents, not data entry.</p>
          </div>
          <div className="relative overflow-hidden rounded-3xl bg-redline-500 text-white p-8 flex flex-col justify-center">
            <div className="absolute -right-10 -bottom-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
            <Building2 className="relative h-8 w-8 text-white/90 mb-4" strokeWidth={1.5} />
            <h3 className="relative font-heading text-xl font-extrabold tracking-tight">Now multiply that across your whole portfolio.</h3>
            <p className="relative mt-3 text-white/90 text-sm leading-relaxed">Every community runs the same playbook. One manager covers more doors, residents get instant answers, and you scale without adding headcount.</p>
          </div>
        </div>

        <PortfolioCalculator />

        <p className="mt-6 text-xs text-muted-foreground">Based on an independent analysis of <span className="font-semibold text-ink-700">904 pages and 977 top-level resident request threads (5,079 messages)</span> from a live community's ticket history.</p>
      </section>

      {/* THE PLATFORM REVEAL — land with the concierge, expand with the command center */}
      <section id="platform" className="bg-ink text-white relative overflow-hidden py-24">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-redline-500/10 blur-3xl" />
        <div className="max-w-6xl mx-auto px-6 relative">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-400 mb-3">Beyond the concierge</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">The concierge is just <span className="text-redline-500">the front door.</span></h2>
            <p className="mt-4 text-sand-300 leading-relaxed">Every call and text it handles becomes a ticket, a booking, a record or a piece of community knowledge — inside one command center for community life. Engage residents, streamline operations, and give your board the tools it's been missing.</p>
          </motion.div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="platform-module-grid">
            {[
              { icon: ClipboardList, name: "Requests", body: "Every call and text becomes a tracked ticket — assigned, prioritized, closed. No sticky notes." },
              { icon: HardHat, name: "Vendors & Work Orders", body: "The fix, not just the answer — insured vendors, crews texted directly, invoices already coded." },
              { icon: Megaphone, name: "Broadcasts", body: "One message to every doorstep — text and email, with consent handled for you." },
              { icon: CalendarCheck, name: "Reservations", body: "The clubhouse, courts and pool book themselves — residents reserve by text." },
              { icon: BarChart3, name: "Surveys", body: "Board decisions with data — send a pulse question, see resident sentiment by morning." },
              { icon: PenLine, name: "E-Sign", body: "Motions approved from a phone — no more print, sign, scan, repeat." },
              { icon: CalendarDays, name: "Events", body: "Publish once; residents ask the concierge, get details and RSVP by text." },
              { icon: BookOpen, name: "Documents & Knowledge", body: "CCRs, minutes and rules — digitized, searchable and cited in every answer." },
              { icon: Scale, name: "Rules", body: "Every rule extracted into plain English residents can actually find and follow." },
              { icon: Contact, name: "Trusted Pros", body: "Vendors, pros, utilities, office hours — one directory the whole community shares." },
              { icon: Globe, name: "Public Page", body: "A clean community website, live in minutes — no webmaster required." },
              { icon: FileBarChart, name: "Reporting", body: "Board-ready proof of value — what was asked, handled and resolved, every month." },
              { icon: UserRound, name: "Resident Portal", body: "Documents, requests and notifications for every household — no app to install." },
            ].map((m, i) => (
              <motion.div key={m.name} custom={i % 4} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
                data-testid={`platform-module-${m.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl bg-white/[0.04] border border-white/10 p-5 hover:bg-white/[0.08] hover:-translate-y-0.5 transition-all duration-300">
                <div className="h-10 w-10 rounded-xl bg-redline-500/15 flex items-center justify-center mb-4">
                  <m.icon className="h-5 w-5 text-redline-400" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading font-extrabold tracking-tight text-white text-[15px] mb-1.5">{m.name}</h3>
                <p className="text-sand-300 text-[13px] leading-relaxed">{m.body}</p>
              </motion.div>
            ))}
          </div>

          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-40px" }}
            className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5 rounded-3xl border border-white/10 bg-white/[0.04] px-7 py-6">
            <p className="flex-1 text-sand-200 leading-relaxed text-[15px]">
              Other platforms built a portal and bolted features on. <span className="text-white font-semibold">Ask Redline built the intelligence first — and the platform grew around it.</span> That's why every module works the way residents already do: one call or text away.
            </p>
            <a href="#contact" {...CAL_ATTRS} data-testid="platform-cta" className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-white text-ink font-semibold px-5 py-3 hover:bg-sand-100 transition-colors">
              See the whole platform <ArrowRight className="h-4 w-4" />
            </a>
          </motion.div>
        </div>
      </section>

      <FieldServices />

      {/* INSTITUTIONAL MEMORY — the moat: knowledge that outlives every board */}
      <section id="memory" className="bg-white border-y border-sand-200 py-24">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">Institutional memory</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Communities forget. <span className="text-redline-500">Yours won't.</span></h2>
            <p className="mt-4 text-ink-700 leading-relaxed">Boards turn over every couple of years. Managers move on. And every handoff loses the "why" — the vendor who fixed the roof, the reasoning behind a rule, the exception granted in 2019. That knowledge walks out the door, and the next board starts from zero.</p>
            <p className="mt-3 text-ink-700 leading-relaxed"><span className="font-semibold text-ink">Ask Redline is your community's permanent memory.</span> Every question answered, request resolved, rule clarified and decision recorded builds into a living intelligence that outlives any board — and any property manager.</p>
            <ul className="mt-6 space-y-3.5">
              {[
                "Boards turn over — the record doesn't",
                "No more “ask the person who left”",
                "Your next board inherits everything on day one",
              ].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[15px] text-ink-700">
                  <History className="h-5 w-5 text-redline-500 shrink-0" strokeWidth={2} /> {t}
                </li>
              ))}
            </ul>
            <p className="mt-7 font-heading text-lg font-extrabold tracking-tight">Every interaction makes the next one easier — that's how communities stay steady.</p>
          </motion.div>

          <motion.div variants={fade} custom={1} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }}
            data-testid="memory-compounding-card" className="relative overflow-hidden rounded-3xl bg-ink text-white p-8">
            <div className="absolute -bottom-20 -right-20 h-56 w-56 rounded-full bg-redline-500/15 blur-3xl" />
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-sand-400 mb-5 relative">Your community gets smarter with every single interaction</p>
            <div className="relative space-y-2.5">
              {[
                { icon: MessageSquareText, event: "Pool hours question answered", gain: "answer remembered" },
                { icon: ClipboardList, event: "Gate repair ticket closed", gain: "vendor & fix on record" },
                { icon: FileText, event: "Board meeting minutes uploaded", gain: "every decision preserved" },
                { icon: Scale, event: "Fence rule clarified for a resident", gain: "precedent saved" },
                { icon: Phone, event: "After-hours call handled", gain: "context added for next time" },
              ].map((s, i) => (
                <motion.div key={s.event} initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ delay: 0.15 + i * 0.16, duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center gap-3.5 rounded-2xl bg-white/[0.05] border border-white/10 px-4 py-3">
                  <div className="h-9 w-9 rounded-xl bg-redline-500/15 flex items-center justify-center shrink-0">
                    <s.icon className="h-4 w-4 text-redline-400" strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] font-semibold text-white leading-tight truncate">{s.event}</p>
                    <p className="text-[12px] text-sand-400 leading-tight">{s.gain}</p>
                  </div>
                  <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-emerald-300 bg-emerald-400/10 rounded-full px-2 py-1">+ knowledge</span>
                </motion.div>
              ))}
            </div>
            <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ delay: 1.1, duration: 0.5 }}
              className="relative mt-5 rounded-2xl border border-redline-500/30 bg-redline-500/10 px-5 py-4 flex items-center gap-4">
              <div className="min-w-0">
                <p className="font-heading text-2xl font-extrabold tracking-tight text-white"><CountUp to={1847} duration={2.2} /><span className="text-redline-400"> and counting</span></p>
                <p className="text-[12px] text-sand-300 mt-0.5">pieces of community knowledge in a typical first year — and none of it walks out the door with a board member or a property manager.</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ONBOARDING */}
      <section id="onboarding" className="bg-ink text-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-400 mb-3">Onboarding</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">Live in <span className="text-redline-500">days,</span> not months.</h2>
            <p className="mt-4 text-sand-300 leading-relaxed">We do the heavy lifting. Here's what standing up a new community looks like end to end.</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {[
              { n: "01", icon: Phone, title: "Kickoff & your number", body: "We provision a dedicated text line for your community and set up your account." },
              { n: "02", icon: ScanText, title: "Free document digitization", body: "Send us your governing docs — even old scans. We OCR and clean them into AI-ready knowledge, at no cost." },
              { n: "03", icon: Users, title: "Roster & consent", body: "Import your residents. Consent is captured, dated and logged for A2P/TCPA compliance." },
              { n: "04", icon: Sparkles, title: "Tune the voice", body: "Set your assistant's persona, tone, coverage hours and which modules each community gets." },
              { n: "05", icon: Zap, title: "Go live", body: "Invite residents by link, QR code or text — no app to install, no logins to manage." },
            ].map((s) => (
              <div key={s.n} className="relative rounded-2xl bg-white/[0.04] border border-white/10 p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-10 w-10 rounded-xl bg-redline-500/15 flex items-center justify-center">
                    <s.icon className="h-5 w-5 text-redline-400" strokeWidth={1.5} />
                  </div>
                  <span className="font-heading font-extrabold text-2xl text-white/10 select-none">{s.n}</span>
                </div>
                <h3 className="font-heading text-base font-bold text-white mb-1.5">{s.title}</h3>
                <p className="text-sand-300 text-[13px] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>

          {/* White-glove onboarding + dedicated Community Success rep */}
          <div data-testid="onboarding-whiteglove" className="mt-6 relative overflow-hidden rounded-3xl border border-redline-500/30 bg-gradient-to-br from-white/[0.07] to-white/[0.02] p-8 sm:p-10">
            <span className="pointer-events-none absolute -top-20 -right-16 h-56 w-56 rounded-full bg-redline-500/20 blur-3xl" />
            <div className="relative flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 rounded-full bg-redline-500/15 text-redline-300 text-[11px] font-bold uppercase tracking-[0.16em] px-3 py-1.5 mb-4">
                  <Sparkles className="h-3.5 w-3.5" strokeWidth={2} /> White-glove, start to finish
                </div>
                <h3 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-white">You're never handed a login and left alone.</h3>
                <p className="mt-3 text-sand-300 leading-relaxed max-w-2xl">Every community gets true <span className="text-white font-semibold">white-glove onboarding</span> — our team does the setup, digitizes your documents, loads your roster and tunes the voice for you. Then a <span className="text-white font-semibold">dedicated Community Success rep</span> stays with you: a real person who knows your community, watches how it's performing, and proactively fine-tunes it so your program keeps getting better long after launch.</p>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4 lg:w-[300px] shrink-0">
                <div className="flex items-start gap-3 rounded-2xl bg-white/[0.05] border border-white/10 p-4">
                  <div className="h-10 w-10 rounded-xl bg-redline-500/20 flex items-center justify-center shrink-0"><HeartHandshake className="h-5 w-5 text-redline-400" strokeWidth={1.75} /></div>
                  <div>
                    <div className="font-heading font-bold text-white text-sm">Done-for-you setup</div>
                    <div className="text-sand-300 text-[13px] leading-snug mt-0.5">We build it with you, hands-on, so go-live is effortless.</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-2xl bg-white/[0.05] border border-white/10 p-4">
                  <div className="h-10 w-10 rounded-xl bg-redline-500/20 flex items-center justify-center shrink-0"><UserRoundCheck className="h-5 w-5 text-redline-400" strokeWidth={1.75} /></div>
                  <div>
                    <div className="font-heading font-bold text-white text-sm">A rep who owns your success</div>
                    <div className="text-sand-300 text-[13px] leading-snug mt-0.5">Ongoing check-ins and tuning to keep results climbing.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INTEGRATIONS */}
      <section id="integrations" className="bg-white border-y border-sand-200 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">Fits your stack</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Works with the portal your community already runs.</h2>
            <p className="text-ink-700 mt-4 leading-relaxed">
              Keep TownSq — or whichever management platform you use — as your system of record. Ask Redline is the resident-facing front door: it answers, captures intake, and routes requests to the right place instead of replacing what already works. When a resident texts a problem, it can <b>open the ticket in your portal and text status updates back</b> as they come in.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
            {[
              ["TownSq", "Available for add-on"],
              ["Vantaca", "Available for add-on"],
              ["AppFolio", "Available for add-on"],
              ["Buildium", "On the roadmap"],
              ["CINC Systems", "On the roadmap"],
              ["FrontSteps", "On the roadmap"],
              ["PayHOA", "On the roadmap"],
              ["Enumerate", "On the roadmap"],
            ].map(([name, note]) => (
              <div key={name} className="rounded-2xl border border-sand-200 bg-sand-50 px-5 py-6 flex flex-col items-center justify-center text-center hover:border-ink transition-colors">
                <span className="font-heading font-extrabold text-lg tracking-tight text-ink">{name}</span>
                <span className={`mt-2 text-[10px] uppercase tracking-wide font-bold px-2 py-0.5 rounded-full ${note === "Available for add-on" ? "bg-redline-50 text-redline-700" : "bg-sand-200 text-ink-600"}`}>{note}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-6">Don't see your platform? If it has an API, we can likely connect it — <a href="#contact" className="text-redline-600 font-semibold hover:underline">tell us what you use</a>.</p>
        </div>
      </section>

      {/* CITED / GOVERNED — legal-comfort positioning for management companies (rule store + audit + portfolio intel) */}
      <section id="citations" className="max-w-6xl mx-auto px-6 py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-500 mb-3">For HOA management companies</p>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Answers your attorney would <span className="text-redline-500">sign off on.</span></h2>
          <p className="text-ink-600 mt-4 leading-relaxed">When a resident asks about a fine, a deadline or a rule, the concierge doesn't paraphrase — it quotes the <b>exact policy and its citation</b>, and only after your team has verified it. Every answer is traceable back to the document it came from.</p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-6 mt-12 items-start">
          {/* Cited-answer mock */}
          <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.5 }}
            data-testid="citations-mock" className="lg:col-span-2 rounded-3xl border border-sand-200 bg-white p-6 md:p-7">
            <div className="flex items-center gap-2 text-xs font-semibold text-ink-500 mb-4"><MessagesSquare className="h-4 w-4 text-redline-500" /> Resident text · live example</div>
            <div className="flex justify-end mb-3">
              <div className="bg-sand-100 text-ink rounded-2xl rounded-br-sm px-4 py-2.5 text-sm max-w-[85%]">Can I put a satellite dish on my balcony?</div>
            </div>
            <div className="flex justify-start">
              <div className="bg-ink text-white rounded-2xl rounded-bl-sm px-4 py-3 text-sm max-w-[92%]">
                Not without ARC approval first — satellite dishes and exterior changes need architectural sign-off before installation.
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-white/10 px-2.5 py-1.5">
                  <FileText className="h-3.5 w-3.5 text-redline-300 shrink-0" />
                  <span className="text-[11px] text-sand-200">Source: <b className="text-white">CC&amp;R §6.4</b> · Architectural</span>
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300"><BadgeCheck className="h-3 w-3" /> Verified</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-ink-500 mt-4 leading-relaxed">Every policy answer carries its citation — so residents get the rule, not an opinion, and you get a record.</p>
          </motion.div>

          {/* Legal-comfort pillars */}
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-5">
            {[
              [Gavel, "Cited to the source", "Fees, deadlines and rules are quoted with their exact section — “prohibited per CC&R \u00a76.4” — never a loose paraphrase a homeowner could dispute."],
              [BadgeCheck, "Nothing goes live until you verify it", "We extract enforceable rules from your CC&Rs into a review queue. A manager verifies each one — only then can the AI cite it. Unsure or unverified? It never guesses on a fine or policy."],
              [ScanText, "A complete audit trail", "Every answer is stored with the rule and section behind it. Show a board — or an attorney — exactly why the concierge said what it said, any time."],
              [Building2, "One standard across every community", "Run 5 communities or 50 — each gets its own dedicated line, documents and HOA manager, all held to the same cited, verified and auditable standard. No community drifts."],
            ].map(([Icon, t, b]) => (
              <motion.div key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4 }}
                data-testid={`citations-pillar`} className="rounded-2xl border border-sand-200 bg-white p-6 hover:border-redline-300 transition-colors">
                <div className="h-10 w-10 rounded-xl bg-redline-50 flex items-center justify-center mb-3"><Icon className="h-5 w-5 text-redline-600" strokeWidth={1.75} /></div>
                <div className="font-heading font-bold text-ink">{t}</div>
                <div className="text-sm text-ink-600 mt-1.5 leading-relaxed">{b}</div>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-2xl border border-sand-200 bg-sand-50 p-5">
          <div className="flex items-center gap-2 text-sm text-ink-700"><ShieldCheck className="h-5 w-5 text-redline-500 shrink-0" /> <span>Grounded, cited, and it never guesses on rules or money — the difference between a chatbot and a concierge you can put in front of homeowners.</span></div>
          <a href="#contact" {...CAL_ATTRS} data-testid="citations-cta" className="sm:ml-auto shrink-0 inline-flex items-center gap-2 rounded-full bg-ink text-white font-semibold px-5 py-2.5 text-sm hover:bg-ink-700 transition-colors">See it on your documents <ArrowRight className="h-4 w-4" /></a>
        </motion.div>
      </section>


      {/* PREMIUM ADD-ONS — DEEP DIVE (both modules) */}
      <section id="security" className="max-w-6xl mx-auto px-6 pb-24">
        <div className="rounded-3xl bg-ink relative overflow-hidden">
          <div className="grain absolute inset-0 opacity-[0.06]" />
          <div className="absolute -top-24 -right-24 h-72 w-72 bg-redline-500/25 blur-3xl rounded-full" />
          <div className="relative p-8 sm:p-12">
            <div className="max-w-2xl">
              <p className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-redline-400 mb-3">
                <Sparkles className="h-4 w-4" strokeWidth={2} /> Premium add-ons · live people, on your hours
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                Add real people to the text line.
              </h2>
              <p className="text-sand-300 mt-4 leading-relaxed">
                Each add-on is configured per community, on the coverage hours you choose. Turn on one, or all of them.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mt-10">
              {/* AI Voice Concierge (flagship) */}
              <div className="bg-white/[0.06] border border-redline-500/40 rounded-2xl p-6 backdrop-blur flex flex-col relative" data-testid="addon-voice-concierge">
                <div className="absolute top-0 right-0 text-[10px] font-bold bg-redline-500 text-white px-3 py-1 rounded-bl-xl rounded-tr-2xl">PREMIUM</div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-11 w-11 rounded-xl bg-redline-500/20 flex items-center justify-center"><Phone className="h-5 w-5 text-redline-400" strokeWidth={1.5} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-redline-400">Premium add-on · live phone answering</div>
                    <div className="font-heading font-bold text-white text-lg">AI Voice Concierge</div>
                  </div>
                </div>
                <p className="text-sand-300 text-sm leading-relaxed">Residents can <span className="text-white font-semibold">call your community number and talk to the AI live</span> — it answers in real time, grounded in your own documents, logs maintenance requests and takes messages. A voicemail and forward-to-a-person safety net always stays underneath.</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {["Answers live inbound calls 24/7", "Grounded in your community docs", "Logs requests & takes messages", "Voicemail/forward safety net + minute caps"].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-sand-100"><Check className="h-4 w-4 mt-0.5 shrink-0 text-redline-400" strokeWidth={2} /> {t}</li>
                  ))}
                </ul>
              </div>

              {/* Live Support */}
              <div className="bg-white/[0.06] border border-white/10 rounded-2xl p-6 backdrop-blur flex flex-col">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-11 w-11 rounded-xl bg-redline-500/20 flex items-center justify-center"><Headset className="h-5 w-5 text-redline-400" strokeWidth={1.5} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-redline-400">Add-on module</div>
                    <div className="font-heading font-bold text-white text-lg">Community Concierge Team</div>
                  </div>
                </div>
                <p className="text-sand-300 text-sm leading-relaxed">Residents get answers <span className="text-white font-semibold">immediately from a real person</span> on your Community Concierge Team — and can open real tickets and work orders — right in the same text thread. Live reps step in during your coverage hours, and the AI pauses while a rep is handling it, then hands back.</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {["Immediate live answers in-thread", "Open real tickets & work orders", "AI pauses on takeover, resumes after", "24/7 or on your shifts"].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-sand-100"><Check className="h-4 w-4 mt-0.5 shrink-0 text-redline-400" strokeWidth={2} /> {t}</li>
                  ))}
                </ul>
              </div>

              {/* Neighborhood Watch (Security Monitoring) */}
              <div className="bg-white/[0.06] border border-redline-500/40 rounded-2xl p-6 backdrop-blur flex flex-col relative">
                <div className="absolute top-0 right-0 text-[10px] font-bold bg-redline-500 text-white px-3 py-1 rounded-bl-xl rounded-tr-2xl">PREMIUM</div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-11 w-11 rounded-xl bg-redline-500/20 flex items-center justify-center"><ShieldAlert className="h-5 w-5 text-redline-400" strokeWidth={1.5} /></div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-redline-400">Premium add-on · live security monitoring</div>
                    <div className="font-heading font-bold text-white text-lg">Neighborhood Watch</div>
                  </div>
                </div>
                <p className="text-sand-300 text-sm leading-relaxed">Turn your whole community into extra eyes. A resident spots something off and <span className="text-white font-semibold">texts a concern or a photo</span> — Redline's live security team triages it, pulls up the cameras in that area, checks the lot or an entry, and responds, dispatches, or sends a community-wide safety alert in real time.</p>
                <ul className="mt-4 space-y-2 flex-1">
                  {["Residents report concerns by text or photo", "Redline's team watches the live cameras", "On-call dispatch & community safety alerts", "Emergencies always routed to 911"].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-sand-100"><Check className="h-4 w-4 mt-0.5 shrink-0 text-redline-400" strokeWidth={2} /> {t}</li>
                  ))}
                </ul>
                <p className="mt-4 text-xs text-sand-200"><span className="font-semibold text-white">Requires active security camera monitoring through Redline Monitoring Services.</span> Already have cameras? We can monitor your existing system — or provide a full setup. <a href="https://redlinemonitoring.com" target="_blank" rel="noopener noreferrer" className="text-redline-400 font-semibold hover:underline">See Redline Monitoring ↗</a>.</p>
              </div>
            </div>

            {/* Field Services — the fourth paid module: the people who actually do the work */}
            <div className="mt-5 bg-white/[0.06] border border-redline-500/40 rounded-2xl p-6 backdrop-blur relative overflow-hidden"
              data-testid="addon-field-services">
              <div className="absolute top-0 right-0 text-[10px] font-bold bg-redline-500 text-white px-3 py-1 rounded-bl-xl rounded-tr-2xl">PREMIUM</div>
              <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-11 w-11 rounded-xl bg-redline-500/20 flex items-center justify-center"><HardHat className="h-5 w-5 text-redline-400" strokeWidth={1.5} /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-redline-400">Premium add-on · vendors, work orders &amp; invoices</div>
                      <div className="font-heading font-bold text-white text-lg">Field Services</div>
                    </div>
                  </div>
                  <p className="text-sand-300 text-sm leading-relaxed">
                    The module that closes the loop: a request becomes <span className="text-white font-semibold">one coded work order</span>,
                    it only goes to a vendor whose insurance is current, the crew on that property gets a text
                    in their language, and the invoice comes back read, proof-checked and coded for your accountant.
                    <span className="text-white font-semibold"> Redline never moves your money.</span>
                  </p>
                  <Link to="/field-services" data-testid="addon-fs-more"
                    className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-redline-400 hover:text-white transition-colors">
                    How the whole chain works <ArrowRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </div>
                <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-5 self-center">
                  {["Insurance gates live work, not email reminders", "Crews texted directly — English or Spanish",
                    "Photo proof closes the resident's ticket", "Projects out for bids, no vendor login needed",
                    "Kickoff won't start without a budget code", "Board signs off above your threshold"].map((t) => (
                    <li key={t} className="flex items-start gap-2 text-sm text-sand-100"><Check className="h-4 w-4 mt-0.5 shrink-0 text-redline-400" strokeWidth={2} /> {t}</li>
                  ))}
                </ul>
              </div>
            </div>

            {/* More premium add-ons (prices kept sales-gated) */}
            <div className="mt-8 pt-8 border-t border-white/10">
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-redline-400 mb-4">More premium add-ons</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-more-addons">
                {[
                  { icon: MessagesSquare, title: "WhatsApp Channel", body: "Residents reach the same concierge over WhatsApp, in addition to SMS — no app, no login." },
                  { icon: TrendingUp, title: "Portfolio Intelligence", body: "Cross-community trends, emerging topics and escalation reasons across your whole book of business." },
                  { icon: Languages, title: "Multi-Language", body: "The assistant auto-mirrors each resident's language across every channel — English, Spanish and more." },
                ].map((a) => (
                  <div key={a.title} className="bg-white/[0.06] border border-white/10 rounded-2xl p-5 backdrop-blur">
                    <div className="h-10 w-10 rounded-xl bg-redline-500/20 flex items-center justify-center mb-3"><a.icon className="h-5 w-5 text-redline-400" strokeWidth={1.5} /></div>
                    <div className="font-heading font-bold text-white text-sm">{a.title}</div>
                    <p className="text-sand-300 text-xs leading-relaxed mt-1.5">{a.body}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <a href="#contact" className="rounded-full bg-redline-500 text-white font-semibold px-7 py-3.5 flex items-center gap-2 w-fit hover:bg-redline-600 transition-colors">
                Talk to us about add-ons <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
              </a>
              <p className="text-sand-400 text-xs max-w-sm">Configured per community. Coverage hours, live support and monitoring are all opt-in.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* CONTACT */}
      <section id="contact" className="bg-white border-t border-sand-200 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-redline-600 mb-3">Let's talk</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Book a demo for your community.</h2>
            <p className="text-ink-700 mt-4 leading-relaxed max-w-2xl">We'll set up your dedicated line, load your documents, and have your concierge answering residents by name — usually within a week. Pick a time on the right, or drop us a note on the left.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            {/* LEFT — message form */}
            <div>
              <div className="mb-4">
                <h3 className="font-heading font-bold text-lg text-ink">Send us a note</h3>
                <p className="text-sm text-ink-600 mt-1">Tell us about your community and we'll reach out within one business day.</p>
              </div>
              <form onSubmit={submit} className="bg-sand-50 rounded-2xl border border-sand-200 p-7 space-y-4">
                {[
                  ["name", "Your name", "text"],
                  ["email", "Work email", "email"],
                  ["community", "Community / HOA name", "text"],
                ].map(([k, label, type]) => (
                  <div key={k}>
                    <label className="text-sm font-semibold text-ink">{label}</label>
                    <input data-testid={`contact-${k}-input`} type={type} value={form[k]}
                      onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="mt-1.5 w-full rounded-xl border border-sand-200 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-redline-500/40 focus:border-redline-500 transition-shadow" />
                  </div>
                ))}
                <div>
                  <label className="text-sm font-semibold text-ink">Anything we should know?</label>
                  <textarea data-testid="contact-message-input" rows={3} value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-1.5 w-full rounded-xl border border-sand-200 bg-white px-4 py-3 outline-none focus:ring-2 focus:ring-redline-500/40 focus:border-redline-500 transition-shadow" />
                </div>
                <button data-testid="contact-submit-btn" disabled={sending}
                  className="w-full rounded-full bg-ink text-white font-semibold py-3.5 hover:bg-ink-700 transition-colors disabled:opacity-60">
                  {sending ? "Sending…" : "Send message"}
                </button>
                <p className="text-[11px] text-ink-500 text-center pt-1">
                  Or email us at <a href="mailto:mike@askredline.com" className="font-semibold text-ink hover:text-redline-600 transition-colors">mike@askredline.com</a>
                </p>
              </form>
            </div>

            {/* RIGHT — book a meeting */}
            <div>
              <div className="mb-4">
                <h3 className="font-heading font-bold text-lg text-ink">Book a meeting</h3>
                <p className="text-sm text-ink-600 mt-1">Pick a time that works — 20-minute walk-through of your community's setup.</p>
              </div>
              <div className="rounded-2xl border border-sand-200 bg-white overflow-hidden shadow-sm">
                <Cal namespace={CAL_NS} calLink={CAL_LINK} style={{ width: "100%", height: "600px", overflow: "scroll" }} config={{ layout: "month_view" }} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <footer className="bg-ink text-sand-300 py-10">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex flex-col sm:flex-row items-center gap-2 text-white text-center sm:text-left">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-redline-500 flex items-center justify-center shrink-0">
                <MessageSquareText className="h-4 w-4" strokeWidth={1.5} />
              </div>
              <span className="font-heading font-bold whitespace-nowrap">Ask Redline</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-center">
            <Link to="/solutions" data-testid="footer-solutions-link" className="whitespace-nowrap hover:text-white transition-colors">Solutions</Link>
            <Link to="/compare" data-testid="footer-compare-link" className="whitespace-nowrap hover:text-white transition-colors">Compare</Link>
            <Link to="/resources" data-testid="footer-resources-link" className="whitespace-nowrap hover:text-white transition-colors">Guides</Link>
            <Link to="/opt-in" data-testid="footer-optin-link" className="whitespace-nowrap hover:text-white transition-colors">SMS Opt-In</Link>
            <Link to="/vendor-network" data-testid="footer-vendor-link" className="whitespace-nowrap hover:text-white transition-colors">Vendors: apply to join</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="whitespace-nowrap hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="whitespace-nowrap hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/dpa" data-testid="footer-dpa-link" className="whitespace-nowrap hover:text-white transition-colors">Data Processing Addendum</Link>
            <span className="whitespace-nowrap">© {new Date().getFullYear()} Ask Redline · Austin, TX</span>
          </div>
        </div>
      </footer>
      <VoiceDemo open={voiceOpen} onClose={() => setVoiceOpen(false)} />
    </div>
  );
}
